/**
 * Read-only CodeMirror-6-Rendering für Code-Blöcke (Syntax-Highlighting, Zeilennummern,
 * One-Dark-Theme). Wird aus der Markdown-Nachbearbeitung aufgerufen (siehe Markdown.tsx),
 * um `<pre><code>` durch eine CodeMirror-Ansicht zu ersetzen.
 */
import { EditorState, type Extension } from "@codemirror/state";
import { EditorView, lineNumbers } from "@codemirror/view";
import { StreamLanguage } from "@codemirror/language";
import { oneDark } from "@codemirror/theme-one-dark";
import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import { json } from "@codemirror/lang-json";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { markdown } from "@codemirror/lang-markdown";
import { sql } from "@codemirror/lang-sql";
import { xml } from "@codemirror/lang-xml";
import { yaml } from "@codemirror/lang-yaml";
import { shell } from "@codemirror/legacy-modes/mode/shell";
import { powerShell } from "@codemirror/legacy-modes/mode/powershell";
import { go } from "@codemirror/legacy-modes/mode/go";
import { rust } from "@codemirror/legacy-modes/mode/rust";
import { ruby } from "@codemirror/legacy-modes/mode/ruby";
import { c, cpp, java, csharp, kotlin } from "@codemirror/legacy-modes/mode/clike";

/** Mappt einen Sprach-Hint (aus ```lang) auf die passende CodeMirror-Sprach-Erweiterung. */
function langExtension(lang: string): Extension | null {
  switch (lang.trim().toLowerCase()) {
    case "js":
    case "javascript":
    case "node":
    case "mjs":
    case "cjs":
      return javascript();
    case "jsx":
      return javascript({ jsx: true });
    case "ts":
    case "typescript":
      return javascript({ typescript: true });
    case "tsx":
      return javascript({ jsx: true, typescript: true });
    case "py":
    case "python":
      return python();
    case "json":
    case "jsonc":
    case "json5":
      return json();
    case "html":
    case "htm":
    case "xhtml":
    case "vue":
    case "svelte":
      return html();
    case "css":
    case "scss":
    case "less":
      return css();
    case "md":
    case "markdown":
      return markdown();
    case "sql":
    case "mysql":
    case "postgres":
    case "postgresql":
      return sql();
    case "xml":
    case "svg":
    case "xsl":
      return xml();
    case "yaml":
    case "yml":
      return yaml();
    case "sh":
    case "bash":
    case "zsh":
    case "shell":
    case "console":
      return StreamLanguage.define(shell);
    case "ps1":
    case "powershell":
      return StreamLanguage.define(powerShell);
    case "go":
    case "golang":
      return StreamLanguage.define(go);
    case "rs":
    case "rust":
      return StreamLanguage.define(rust);
    case "rb":
    case "ruby":
      return StreamLanguage.define(ruby);
    case "c":
    case "h":
      return StreamLanguage.define(c);
    case "cpp":
    case "c++":
    case "cc":
    case "hpp":
    case "cxx":
      return StreamLanguage.define(cpp);
    case "java":
      return StreamLanguage.define(java);
    case "cs":
    case "csharp":
    case "c#":
      return StreamLanguage.define(csharp);
    case "kt":
    case "kotlin":
      return StreamLanguage.define(kotlin);
    default:
      return null; // unbekannte Sprache → kein Highlighting, aber Theme + Zeilennummern
  }
}

/** Erstellt eine read-only CodeMirror-Ansicht für `code` in `parent`. Caller ruft `.destroy()`. */
export function mountCodeBlock(parent: HTMLElement, code: string, lang: string): EditorView {
  const ext = langExtension(lang);
  const state = EditorState.create({
    doc: code,
    extensions: [
      lineNumbers(),
      EditorView.editable.of(false),
      EditorState.readOnly.of(true),
      EditorView.lineWrapping, // lange Zeilen umbrechen statt horizontal scrollen
      oneDark,
      ...(ext ? [ext] : []),
    ],
  });
  return new EditorView({ state, parent });
}
